import { computed, inject, Injectable } from '@angular/core';
import { CurrentUrlService } from '@cumlaude/shared-services';
import { UserRoutesService } from './user-routes.service';
import { UserMenuService } from './user-menu.service';
import { NavigatieUitklapService } from './navigatie-uitklap.service';
import { UrlService } from './url.service';
import { bouwNavigatieItem, NavigatieItem, NavigatieRij, platteRijen } from '../layout/hoofdnavigatie/navigatie-model';

export type NavigatieSectie = 'dashboards' | 'beheer';

/**
 * De verticale hoofdnavigatie: leidt de navigatieboom af uit UserRoutesService.activeAuthorisedRoutes
 * (dus met dezelfde rechtenfiltering als de horizontale navigatie -- geen eigen rechtencheck) en
 * combineert die met de open-set uit NavigatieUitklapService tot de platte, zichtbare rijenlijst.
 *
 * Toont de Dashboards- of de Beheer-sectie (routeData.base 'core' resp. 'beheer'), afhankelijk van
 * UrlService.isBeheer() -- dezelfde signal waarmee de oude header al schakelt.
 */
@Injectable({
	providedIn: 'root',
})
export class HoofdnavigatieService {
	private readonly routes = inject(UserRoutesService);
	private readonly menu = inject(UserMenuService);
	private readonly uitklap = inject(NavigatieUitklapService);
	private readonly currentUrl = inject(CurrentUrlService);
	private readonly urlService = inject(UrlService);

	readonly sectie = computed<NavigatieSectie>(() => (this.urlService.isBeheer() ? 'beheer' : 'dashboards'));

	/**
	 * De tree, zonder queryparams (die zijn contextafhankelijk, zie zichtbareRijen).
	 */
	private readonly tree = computed<readonly NavigatieItem[]>(() => {
		const base = this.sectie() === 'beheer' ? 'beheer' : 'core';
		return this.routes
			.activeAuthorisedRoutes()
			.filter((route) => route.routeData!.base === base && route.path !== 'start')
			.map((route) => bouwNavigatieItem(route));
	});

	/**
	 * De zichtbare rijen: de tree platgeslagen op de open-set, met queryparams per item via
	 * UserMenuService -- dezelfde matching als de horizontale navigatie, zodat filters niet
	 * resetten bij het wisselen van dashboard.
	 */
	readonly zichtbareRijen = computed<NavigatieRij[]>(() =>
		platteRijen(this.tree(), this.uitklap.open(), this.currentUrl.currentPath(), (item) => ({
			queryParams: item.topLevel ? this.menu.matchTopLevelMenuQueryParams(item.firstLeaf) : this.menu.matchTabMenuQueryParams(item.firstLeaf),
			queryParamsHandling: 'merge',
		}))
	);

	readonly magAllesDichtklappen = computed(() => this.zichtbareRijen().some((rij) => rij.niveau === 0 && rij.uitgeklapt));

	/** De Startpagina-route, uit hetzelfde geautoriseerde signal zodat url en naam op één plek staan. */
	readonly startRoute = computed(() => this.routes.activeAuthorisedRoutes().find((route) => route.path === 'start'));
}
